import { Component, ElementRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  NewTenantRequest,
  TenantResponse,
  TenantsService,
} from 'src/app/services/tenants.service';
import {
  Distributor,
  DistributorsService,
} from 'src/app/services/distributors.service';
import {
  Franchise,
  FranchisesService,
} from 'src/app/services/franchises.service';
import {
  CassRequest,
  CassResponse,
  CassService,
} from 'src/app/services/cass.service';
import { states } from 'src/app/utils/state-list';

@Component({
  selector: 'app-create-tenant',
  templateUrl: './create-tenant.component.html',
  styleUrls: ['./create-tenant.component.scss'],
})
export class CreateTenantComponent {
  @ViewChild('cassModal') cassModal!: ElementRef<HTMLDialogElement>;

  loadingDistributors = false;
  loadingFranchises = false;

  distributors: Distributor[] = [];
  franchises: Franchise[] = [];

  formGroup = new FormGroup({
    accountNumber: new FormControl('', [
      this.accountNumberValidator.bind(this),
    ]),
    quickbooksId: new FormControl('', [this.quickbooksIdValidator.bind(this)]),
    distributor: new FormControl('', [Validators.required]),
    franchise: new FormControl(''),
    companyName: new FormControl('', [Validators.required]),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    phone: new FormControl('', [Validators.required, this.phoneValidator]),
    address: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    st: new FormControl('', [Validators.required]),
    zip: new FormControl('', [Validators.required]),
    billingAddress: new FormControl('', [Validators.required]),
    billingCity: new FormControl('', [Validators.required]),
    billingSt: new FormControl('', [Validators.required]),
    billingZip: new FormControl('', [Validators.required]),
  });

  billingSameAsAddress = false;
  isExistingTenant = false;

  get accountNumber() {
    return this.formGroup.get('accountNumber')!;
  }
  get quickbooksId() {
    return this.formGroup.get('quickbooksId')!;
  }
  get distributor() {
    return this.formGroup.get('distributor')!;
  }
  get franchise() {
    return this.formGroup.get('franchise')!;
  }
  get companyName() {
    return this.formGroup.get('companyName')!;
  }
  get firstName() {
    return this.formGroup.get('firstName')!;
  }
  get lastName() {
    return this.formGroup.get('lastName')!;
  }
  get email() {
    return this.formGroup.get('email')!;
  }
  get phone() {
    return this.formGroup.get('phone')!;
  }
  get address() {
    return this.formGroup.get('address')!;
  }
  get city() {
    return this.formGroup.get('city')!;
  }
  get st() {
    return this.formGroup.get('st')!;
  }
  get zip() {
    return this.formGroup.get('zip')!;
  }
  get billingAddress() {
    return this.formGroup.get('billingAddress')!;
  }
  get billingCity() {
    return this.formGroup.get('billingCity')!;
  }
  get billingSt() {
    return this.formGroup.get('billingSt')!;
  }
  get billingZip() {
    return this.formGroup.get('billingZip')!;
  }

  states = states;

  validatingShippingAddress = false;
  cassShippingInputAddress?: CassRequest;
  cassShippingResponse?: CassResponse;
  cassShippingError = '';
  useCassShippingResponse = true;

  validatingBillingAddress = false;
  cassBillingInputAddress?: CassRequest;
  cassBillingResponse?: CassResponse;
  cassBillingError = '';
  useCassBillingResponse = true;

  creatingTenant = false;
  createResponse?: TenantResponse;
  createError = '';

  constructor(
    private cassService: CassService,
    private distributorsService: DistributorsService,
    private franchisesService: FranchisesService,
    private tenantsService: TenantsService
  ) {
    this.loadDistributors();
    this.loadFranchises();
  }

  loadDistributors() {
    this.loadingDistributors = true;
    this.distributorsService.getAllDistributors().subscribe({
      next: (res) => {
        console.log('Response from server: ', res);
        this.distributors = res.distributors;
      },
      error: (err) => {
        console.error(err);
      },
      complete: () => {
        this.loadingDistributors = false;
      },
    });
  }

  loadFranchises() {
    this.loadingFranchises = true;
    this.franchisesService.getAllFranchises().subscribe({
      next: (res) => {
        console.log('Response from server: ', res);
        this.franchises = res.franchises;
      },
      error: (err) => {
        console.error(new Error(err));
      },
      complete: () => {
        this.loadingFranchises = false;
      },
    });
  }

  accountNumberValidator(control: AbstractControl) {
    if (!this.isExistingTenant) {
      // not required if not an existing tenant
      return null;
    }

    if (!this.tenantsService.validateAccountNumber(control.value!)) {
      return { accountNumberError: true };
    }

    return null;
  }

  quickbooksIdValidator(control: AbstractControl) {
    if (!this.isExistingTenant) {
      // not required if not an existing tenant
      return null;
    }

    if (control.value.length === 0) {
      return { quickbooksIdError: true };
    }
    if (!/^\d+$/.test(control.value)) {
      return { quickbooksIdError: true };
    }

    return null;
  }

  phoneValidator(control: AbstractControl) {
    if (control.value.replace(/\D/g, '').length !== 10) {
      return { phoneNumberError: true };
    }
    return null;
  }

  handleBillingSameAsAddressChecked(ev: Event) {
    const checked = (ev.target as HTMLInputElement).checked;
    this.billingSameAsAddress = checked;
    if (checked) {
      this.billingAddress.setValue(this.address.value!);
      this.billingAddress.disable();
      this.billingCity.setValue(this.city.value!);
      this.billingCity.disable();
      this.billingSt.setValue(this.st.value!);
      this.billingSt.disable();
      this.billingZip.setValue(this.zip.value!);
      this.billingZip.disable();
    } else {
      this.billingAddress.setValue('');
      this.billingAddress.enable();
      this.billingCity.setValue('');
      this.billingCity.enable();
      this.billingSt.setValue('');
      this.billingSt.enable();
      this.billingZip.setValue('');
      this.billingZip.enable();
    }
  }

  handleAddressChange() {
    // this is used to update the billing address fields when the shipping
    // address fields are changed
    if (this.billingSameAsAddress) {
      this.billingAddress.setValue(this.address.value!);
      this.billingCity.setValue(this.city.value!);
      this.billingSt.setValue(this.st.value!);
      this.billingZip.setValue(this.zip.value!);
    }
  }

  validateAddress() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.validatingShippingAddress = true;
    this.cassShippingInputAddress = undefined;
    this.cassShippingResponse = undefined;
    this.cassShippingError = '';
    this.cassModal.nativeElement.showModal();

    const cassShippingReq: CassRequest = {
      company: this.companyName.value!,
      address: this.address.value!,
      city: this.city.value!,
      state: this.st.value!,
      zip: this.zip.value!,
    };

    this.cassShippingInputAddress = cassShippingReq;

    console.log('CASS Shipping Request: ', cassShippingReq);

    this.cassService.validateAddress(cassShippingReq).subscribe({
      next: (res) => {
        console.log('CASS Shipping Response: ', res);
        this.validatingShippingAddress = false;
        this.cassShippingError = '';
        this.cassShippingResponse = res;
      },
      error: (err) => {
        console.error(err);
        this.validatingShippingAddress = false;
        this.cassShippingError = err.message;
      },
    });

    if (!this.billingSameAsAddress) {
      this.validatingBillingAddress = true;
      this.cassBillingInputAddress = undefined;
      this.cassBillingResponse = undefined;
      this.cassBillingError = '';

      const cassBillingReq: CassRequest = {
        company: '',
        address: this.billingAddress.value!,
        city: this.billingCity.value!,
        state: this.billingSt.value!,
        zip: this.billingZip.value!,
      };

      this.cassBillingInputAddress = cassBillingReq;

      console.log('CASS Billing Request: ', cassBillingReq);

      this.cassService.validateAddress(cassBillingReq).subscribe({
        next: (res) => {
          console.log('CASS Billing Response: ', res);
          this.validatingBillingAddress = false;
          this.cassBillingError = '';
          this.cassBillingResponse = res;
        },
        error: (err) => {
          console.error(err);
          this.validatingBillingAddress = false;
          this.cassBillingError = err.message;
        },
      });
    } else {
      this.validatingBillingAddress = false;
      this.cassBillingInputAddress = undefined;
      this.cassBillingResponse = undefined;
      this.cassBillingError = '';
    }
  }

  closeCassModal() {
    this.cassModal.nativeElement.close();
  }

  createTenant() {
    this.createError = '';
    this.closeCassModal();

    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const createRequest: NewTenantRequest = {
      distributor_id: this.distributor.value!,
      franchise_id: this.franchise.value,
      company_name: this.companyName.value!,
      first_name: this.firstName.value!,
      last_name: this.lastName.value!,
      email: this.email.value!,
      phone: this.phone.value!,
      address: this.address.value!,
      city: this.city.value!,
      st: this.st.value!,
      zip: this.zip.value!,
      billing_address: this.billingAddress.value!,
      billing_city: this.billingCity.value!,
      billing_st: this.billingSt.value!,
      billing_zip: this.billingZip.value!,
      send_invite: false,
    };

    if (this.useCassShippingResponse) {
      createRequest.company_name = this.cassShippingResponse!.company;
      createRequest.address = this.cassShippingResponse!.address;
      createRequest.city = this.cassShippingResponse!.city;
      createRequest.st = this.cassShippingResponse!.state;
      createRequest.zip = this.cassShippingResponse!.zip;
    }

    if (this.useCassBillingResponse) {
      if (this.billingSameAsAddress) {
        createRequest.billing_address = this.cassShippingResponse!.address;
        createRequest.billing_city = this.cassShippingResponse!.city;
        createRequest.billing_st = this.cassShippingResponse!.state;
        createRequest.billing_zip = this.cassShippingResponse!.zip;
      } else {
        createRequest.billing_address = this.cassBillingResponse!.address;
        createRequest.billing_city = this.cassBillingResponse!.city;
        createRequest.billing_st = this.cassBillingResponse!.state;
        createRequest.billing_zip = this.cassBillingResponse!.zip;
      }
    }

    if (this.isExistingTenant) {
      createRequest.account_number = this.accountNumber.value!;
      createRequest.avatax_customer_code = this.quickbooksId.value!;
    }

    console.log(`Creating tenant with `, createRequest);

    this.creatingTenant = true;
    this.tenantsService.createTenant(createRequest).subscribe({
      next: (res) => {
        this.creatingTenant = false;
        console.log('Response from server: ', res);
        this.createResponse = res;
        this.resetValues();
      },
      error: (err) => {
        this.creatingTenant = false;
        console.error(err);

        if (typeof err === 'string') {
          if (err.includes('quickbooks customer not created')) {
            this.createError =
              'Tenant was created successfully, but failed to create ' +
              'Quickbooks customer.';
            return;
          } else if (err.includes('quickbooks customer id not updated')) {
            this.createError =
              'Tenant and Quickbooks Customer were created successfully, but ' +
              'failed to update Tenant with the Quickbooks customer ID.';
            return;
          }
        }
        this.createError = err;
      },
    });
  }

  resetValues() {
    this.formGroup.reset();
    this.formGroup.get('distributor')?.reset('');
    this.formGroup.get('franchise')?.reset('');
    this.formGroup.get('companyName')?.reset('');
    this.formGroup.get('firstName')?.reset('');
    this.formGroup.get('lastName')?.reset('');
    this.formGroup.get('email')?.reset('');
    this.formGroup.get('phone')?.reset('');
    this.formGroup.get('address')?.reset('');
    this.formGroup.get('city')?.reset('');
    this.formGroup.get('st')?.reset('');
    this.formGroup.get('zip')?.reset('');
  }

  refreshPage() {
    window.location.reload();
  }
}
